import React, { useState } from 'react';
import logoSrc from './assets/tri-harder-2.svg';
import connectWithStravaBtn from './assets/btn_strava_connectwith_orange.svg';
import { GOAL_OPTIONS, writePendingOnboarding } from './utils/onboarding';

const CheckIcon = () => (
  <svg className="w-3 h-3 text-fg-on-action" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const InfoButton = ({ id, activeTooltip, setActiveTooltip, content }) => (
  <div className="relative flex-shrink-0">
    <button
      onMouseEnter={() => setActiveTooltip(id)}
      onMouseLeave={() => setActiveTooltip(null)}
      onFocus={() => setActiveTooltip(id)}
      onBlur={() => setActiveTooltip(null)}
      className="w-5 h-5 rounded-full bg-surface-hover text-fg-muted hover:bg-neutral-500 hover:text-fg-on-action flex items-center justify-center text-xs font-bold transition-colors cursor-default select-none"
      aria-label="More information"
      tabIndex={0}
    >
      i
    </button>
    {activeTooltip === id && (
      <div className="absolute bottom-7 right-0 w-56 bg-surface-raised border border-border-strong rounded-xl p-3 text-xs text-fg-secondary leading-relaxed z-20 shadow-2xl pointer-events-none">
        {content}
        {/* caret */}
        <div className="absolute top-full right-2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-600" />
      </div>
    )}
  </div>
);

const features = [
  "Deep analysis of your training data",
  "Personalised AI coaching conversations",
  "Race planning & periodization",
  "Downloadable PDF coaching reports",
  "Coaching memory that learns about you over time",
];

const TOOLTIPS = {
  strava: (
    <>
      <strong className="text-fg block mb-1">Strava</strong>
      Recommended for most users. Securely log in with your Strava account.
      Includes full cloud sync — your coaching memory is available across all your devices.
    </>
  ),
  intervals: (active) => (
    <>
      <strong className="text-fg block mb-1">intervals.icu (OAuth)</strong>
      {active
        ? 'Log in securely with your intervals.icu account. Includes full cloud sync.'
        : 'Log in securely with your intervals.icu account. Includes full cloud sync. OAuth registration is pending — check back soon.'}
    </>
  ),
  apikey: (
    <>
      <strong className="text-fg block mb-1">intervals.icu API key</strong>
      A quick manual option for intervals.icu users. Your API key is found under{' '}
      <span className="text-feedback-info-text">intervals.icu → Settings → Developer Settings</span>.
      <br /><br />
      <span className="text-feedback-warning-text">Note:</span> coaching data is saved on this device only —
      no cross-device sync. Connect via OAuth later to unlock full features.
    </>
  ),
};

const SplashPage = ({
  onConnect,
  onConnectIntervals,
  onConnectApiKey,
  onDemo,
  onEnter,
  isConnected,
  isProcessingCallback,
  intervalsOAuthAvailable,
}) => {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showApiKeyForm, setShowApiKeyForm] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [athleteId, setAthleteId] = useState('');
  const [apiKeyError, setApiKeyError] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [intendedGoal, setIntendedGoal] = useState(null);

  // Persist the pending onboarding goal at the moment the user actually
  // commits to a connection method (not on chooser click) — avoids leaking
  // a stale choice to the next person who opens this browser.
  const stampPendingGoal = (source) => {
    if (intendedGoal) writePendingOnboarding(intendedGoal, source);
  };

  const handleStravaClick = () => { stampPendingGoal('strava'); onConnect(); };
  const handleIntervalsClick = () => { stampPendingGoal('intervals'); onConnectIntervals(); };
  const handleDemoClick = () => { stampPendingGoal('demo'); onDemo(); };

  const handleApiKeySubmit = async (e) => {
    e.preventDefault();
    if (!apiKey.trim() || !athleteId.trim()) {
      setApiKeyError('Both fields are required.');
      return;
    }
    setApiKeyError('');
    setApiKeyLoading(true);
    stampPendingGoal('apikey');
    const result = await onConnectApiKey(apiKey.trim(), athleteId.trim());
    if (!result.success) {
      setApiKeyError(result.error || 'Invalid credentials — please check and try again.');
      setApiKeyLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6 py-12 splash-gradient">

      {/* Logo */}
      <div className="bg-white rounded-2xl px-6 py-4 mb-8 shadow-2xl shadow-black/30 splash-logo">
        <img src={logoSrc} alt="Tri Harder" className="h-16 w-auto" />
      </div>

      {/* Tagline */}
      <p className="text-xl text-feedback-info-text mb-10 text-center max-w-sm leading-relaxed">
        AI-powered coaching for triathletes, runners, cyclists &amp; swimmers
      </p>

      {/* Goal chooser — helps tailor onboarding. Only shown pre-connect. */}
      {!isConnected && !isProcessingCallback && (
        <div className="w-full max-w-xs mb-8" role="radiogroup" aria-label="What are you training for?">
          <p className="text-fg-secondary text-xs uppercase tracking-wider text-center mb-3 font-semibold">
            What are you training for?
          </p>
          <div className="grid grid-cols-2 gap-2">
            {GOAL_OPTIONS.map(opt => {
              const selected = intendedGoal === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setIntendedGoal(selected ? null : opt.key)}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-all ${
                    selected
                      ? 'bg-action border-border-focus text-fg-on-action shadow-md'
                      : 'bg-surface-raised/60 border-border hover:border-border-strong text-fg-secondary hover:text-fg'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-base leading-none" aria-hidden="true">{opt.icon}</span>
                    <span className="text-[13px] font-semibold leading-tight">{opt.label}</span>
                  </div>
                  <span className={`text-[10px] leading-snug block ${selected ? 'text-fg-on-action/85' : 'text-fg-subtle'}`}>
                    {opt.sublabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Features — collapsed for unauthenticated users to keep CTAs above fold */}
      {(isConnected || isProcessingCallback) && (
        <div className="space-y-3 mb-10 w-full max-w-xs">
          {features.map((feature, i) => (
            <div key={i} className="flex items-start space-x-3">
              <div className="w-5 h-5 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckIcon />
              </div>
              <span className="text-fg-secondary text-sm leading-relaxed">{feature}</span>
            </div>
          ))}
        </div>
      )}

      {/* CTA */}
      {isProcessingCallback ? (
        <div className="w-full max-w-xs flex items-center justify-center space-x-3 py-4">
          <div className="w-5 h-5 border-2 border-action-link border-t-transparent rounded-full animate-spin" />
          <span className="text-feedback-info-text text-sm">Connecting...</span>
        </div>
      ) : isConnected ? (
        <button
          onClick={onEnter}
          className="w-full max-w-xs bg-action hover:bg-action-hover text-fg-on-action font-bold py-4 px-8 rounded-2xl text-lg shadow-2xl transition-all duration-200 active:scale-95 splash-enter-btn"
        >
          Enter app →
        </button>
      ) : (
        <div className="w-full max-w-xs flex flex-col items-center space-y-4">

          {/* Strava */}
          <div className="flex items-center w-full space-x-2">
            <button
              onClick={handleStravaClick}
              className="flex-1 active:scale-95 transition-transform duration-150 flex justify-center"
              aria-label="Connect with Strava"
            >
              <img src={connectWithStravaBtn} alt="Connect with Strava" className="h-14 w-auto" />
            </button>
            <InfoButton id="strava" activeTooltip={activeTooltip} setActiveTooltip={setActiveTooltip} content={TOOLTIPS.strava} />
          </div>

          <div className="flex items-center w-full space-x-3">
            <div className="flex-1 h-px bg-surface-hover" />
            <span className="text-fg-subtle text-xs">or</span>
            <div className="flex-1 h-px bg-surface-hover" />
          </div>

          {/* intervals.icu OAuth */}
          <div className="flex items-center w-full space-x-2">
            <button
              onClick={intervalsOAuthAvailable ? handleIntervalsClick : undefined}
              disabled={!intervalsOAuthAvailable}
              className={`flex-1 flex items-center justify-center space-x-3 font-semibold py-3 px-6 rounded-xl shadow-lg transition-all duration-150
                ${intervalsOAuthAvailable
                  ? 'bg-action hover:bg-primary-500 active:scale-95 text-fg-on-action'
                  : 'bg-surface-hover text-fg-muted cursor-not-allowed'}`}
              aria-label="Connect with intervals.icu"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current flex-shrink-0" aria-hidden="true">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
              </svg>
              <span>
                Connect with intervals.icu
                {!intervalsOAuthAvailable && <span className="ml-2 text-xs font-normal opacity-70">(coming soon)</span>}
              </span>
            </button>
            <InfoButton id="intervals" activeTooltip={activeTooltip} setActiveTooltip={setActiveTooltip} content={TOOLTIPS.intervals(intervalsOAuthAvailable)} />
          </div>

          <div className="flex items-center w-full space-x-3">
            <div className="flex-1 h-px bg-surface-hover" />
            <span className="text-fg-subtle text-xs">or</span>
            <div className="flex-1 h-px bg-surface-hover" />
          </div>

          {/* API key — tertiary option */}
          <div className="w-full">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowApiKeyForm(v => !v)}
                className="flex-1 flex items-center justify-between text-fg-muted hover:text-fg transition-colors text-sm py-1 px-1"
              >
                <span>Use an intervals.icu API key</span>
                <ChevronIcon open={showApiKeyForm} />
              </button>
              <InfoButton id="apikey" activeTooltip={activeTooltip} setActiveTooltip={setActiveTooltip} content={TOOLTIPS.apikey} />
            </div>

            {showApiKeyForm && (
              <form onSubmit={handleApiKeySubmit} className="mt-3 space-y-3">
                <div>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="API key"
                    className="w-full bg-surface-raised border border-border-strong text-fg placeholder-fg-subtle rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-border-focus focus:ring-1 focus:ring-border-focus"
                    autoComplete="off"
                  />
                  <p className="text-fg-subtle text-xs mt-1 px-1">
                    Found in intervals.icu → Settings → Developer Settings
                  </p>
                </div>
                <div>
                  <input
                    type="text"
                    value={athleteId}
                    onChange={e => setAthleteId(e.target.value)}
                    placeholder="Athlete ID  (e.g. i341513)"
                    className="w-full bg-surface-raised border border-border-strong text-fg placeholder-fg-subtle rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-border-focus focus:ring-1 focus:ring-border-focus"
                    autoComplete="off"
                  />
                  <p className="text-fg-subtle text-xs mt-1 px-1">
                    Visible in your intervals.icu profile URL
                  </p>
                </div>

                {apiKeyError && (
                  <p className="text-feedback-danger-icon text-xs px-1">{apiKeyError}</p>
                )}

                <div className="flex items-start space-x-2 bg-feedback-warning-bg/40 border border-feedback-warning-border/50 rounded-lg px-3 py-2">
                  <span className="text-feedback-warning-icon text-xs mt-0.5">⚠</span>
                  <p className="text-feedback-warning-text/80 text-xs leading-relaxed splash-warning">
                    Coaching data will be stored on this device only. No cross-device sync.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={apiKeyLoading}
                  className="w-full bg-neutral-600 hover:bg-neutral-500 disabled:opacity-50 text-fg-on-action font-semibold py-2.5 rounded-lg text-sm transition-colors active:scale-95"
                >
                  {apiKeyLoading ? 'Connecting...' : 'Connect'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {!isConnected && !isProcessingCallback && (
        <button
          onClick={handleDemoClick}
          className="mt-6 w-full max-w-xs bg-transparent border border-border-strong hover:border-action hover:text-action-link text-fg-secondary font-medium py-3 rounded-xl text-sm transition-colors"
        >
          Try without connecting →
        </button>
      )}

      <p className="text-fg-subtle text-xs mt-6 text-center max-w-xs leading-relaxed">
        Connect your training platform to get personalised coaching.
        Your data stays private and is only used for your coaching sessions.
      </p>
    </div>
  );
};

export default SplashPage;
